// Centralne helpery SEO dla TanStack Start head()
export const SITE_URL = "https://tanielatanie.pl";
export const SITE_NAME = "Tanie Latanie";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.jpg`;

export interface HreflangAlternate {
  hrefLang: string; // np. "pl", "pl-PL", "en", "en-GB", "x-default"
  href: string; // pełny absolutny URL
}

export interface SeoMetaOptions {
  title: string;
  description: string;
  path: string; // np. "/about"
  image?: string; // pełny URL
  type?: "website" | "article";
  noindex?: boolean;
  publishedTime?: string;
  /**
   * Dodatkowe wersje językowe strony. Domyślnie strona dostaje pl, pl-PL
   * oraz x-default wskazujące na canonical URL (PL to jedyny obsługiwany
   * język serwisu). Przekaż tablicę, aby dołączyć tłumaczenia z innych domen
   * lub ścieżek; canonical/x-default i tak są dodawane automatycznie.
   */
  alternates?: HreflangAlternate[];
}

export function buildMeta(o: SeoMetaOptions) {
  const url = `${SITE_URL}${o.path}`;
  const image = o.image ?? DEFAULT_OG_IMAGE;
  const meta: Array<Record<string, string>> = [
    { title: o.title },
    { name: "description", content: o.description },
    { name: "robots", content: o.noindex ? "noindex,follow" : "index,follow" },
    // Open Graph
    { property: "og:title", content: o.title },
    { property: "og:description", content: o.description },
    { property: "og:type", content: o.type ?? "website" },
    { property: "og:url", content: url },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:locale", content: "pl_PL" },
    { property: "og:image", content: image },
    { property: "og:image:width", content: "1536" },
    { property: "og:image:height", content: "1024" },
    { property: "og:image:type", content: "image/jpeg" },
    { property: "og:image:alt", content: o.title },
    // Twitter
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: o.title },
    { name: "twitter:description", content: o.description },
    { name: "twitter:image", content: image },
  ];
  if (o.publishedTime) {
    meta.push({ property: "article:published_time", content: o.publishedTime });
  }
  // Buduj hreflang z deduplikacją (pierwsze wystąpienie wygrywa).
  // Domyślnie strona ma trzy alternate: pl, pl-PL i x-default → canonical.
  // Każda przekazana wersja językowa nadpisuje wartości domyślne.
  const seen = new Set<string>();
  const hreflangs: HreflangAlternate[] = [];
  const pushAlt = (hrefLang: string, href: string) => {
    const key = hrefLang.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    hreflangs.push({ hrefLang, href });
  };
  // Najpierw jawne alternaty (mogą wskazać inną domenę dla pl-PL).
  for (const a of o.alternates ?? []) pushAlt(a.hrefLang, a.href);
  // Defaulty wskazujące na canonical.
  pushAlt("pl", url);
  pushAlt("pl-PL", url);
  pushAlt("x-default", url);

  return {
    meta,
    links: [
      { rel: "canonical", href: url },
      ...hreflangs.map((a) => ({ rel: "alternate", hrefLang: a.hrefLang, href: a.href })),
    ],
    scripts: [] as Array<{ type: string; children: string }>,
  };
}
